import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Button, Alert, Space, Divider } from 'antd';
import { GoogleOutlined, LoadingOutlined } from '@ant-design/icons';
import { useAuth } from '../contexts/AuthContext';
import { googleAuth } from '../services/auth';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated, isLoading } = useAuth();
  const [authLoading, setAuthLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/dashboard');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Handle OAuth callback
  useEffect(() => {
    const token = searchParams.get('token');
    const error = searchParams.get('error');

    if (error) {
      setError(`Authentication failed: ${error}`);
      return;
    }

    if (token) {
      login(token);
      googleAuth.cleanupCallback();
      navigate('/dashboard');
    }
  }, [searchParams, login, navigate]);

  const handleGoogleLogin = async () => {
    try {
      setAuthLoading(true);
      setError(null);
      await googleAuth.initiateLogin();
    } catch (error: any) {
      setError(error.message || 'Failed to start authentication process');
      setAuthLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'var(--bg-secondary)'
      }}>
        <LoadingOutlined style={{ fontSize: 48, color: 'var(--color-positive)' }} />
      </div>
    );
  }

  return (
    <div style={{
      height: '100vh',
      background: 'var(--bg-secondary)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px'
    }}>
      <Card 
        style={{ 
          width: 400, 
          maxWidth: '100%',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}
      >
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div className="text-h2" style={{ color: 'var(--color-positive)', marginBottom: '8px' }}>
            Atlas
          </div>
          <div className="text-card-body" style={{ color: 'var(--text-secondary)' }}>
            Freight Management System
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert
            message="Authentication Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}

        {/* Login Content */}
        <div>
          <div className="text-card-title" style={{ textAlign: 'center', marginBottom: '24px' }}>
            Sign in to your account
          </div>

          <Space direction="vertical" style={{ width: '100%' }} size="large">
            {/* Google Sign In */}
            <Button
              type="primary"
              size="large"
              icon={authLoading ? <LoadingOutlined /> : <GoogleOutlined />}
              onClick={handleGoogleLogin}
              disabled={authLoading}
              style={{
                width: '100%',
                height: '48px',
                fontSize: '16px',
                background: '#4285f4',
                borderColor: '#4285f4',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px'
              }}
            >
              {authLoading ? 'Connecting...' : 'Continue with Google'}
            </Button>

            <Divider>
              <span className="text-card-body" style={{ color: 'var(--text-tertiary)' }}>
                Secure single sign-on
              </span>
            </Divider>

            {/* Info */}
            <div className="text-card-body" style={{ 
              color: 'var(--text-secondary)', 
              textAlign: 'center',
              lineHeight: '1.6'
            }}>
              Access to Atlas requires Google Workspace authentication.
              Contact your administrator if you need access.
            </div>
          </Space>
        </div>

        {/* Footer */}
        <div style={{ 
          textAlign: 'center', 
          marginTop: '32px', 
          paddingTop: '24px',
          borderTop: '1px solid var(--border-default)'
        }}>
          <div className="text-card-body" style={{ color: 'var(--text-tertiary)', fontSize: '12px' }}>
            Atlas V2 - Powered by Rimtrade
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Login;